/**
 * Features Page
 * v1.01 - Mobile responsive: py/px breakpoints, h3 font-size responsive - 2026-03-20
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Container, Typography, Button, Grid, Card } from '@mui/material';

const Features: React.FC = () => {
  const navigate = useNavigate();

  // v2.55 - Use Clerk login page - 2026-02-04
  const handleSignIn = () => {
    navigate('/login');
  };

  return (
    <Box>
      {/* Hero Section */}
      <Box sx={{ bgcolor: 'background.lightBlue', py: { xs: 5, md: 8 }, px: { xs: 2, md: 0 } }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            align="center"
            sx={{ mb: 2, color: 'secondary.main', fontWeight: 700, fontSize: { xs: '1.75rem', sm: '2.25rem', md: '3rem' } }}
          >
            Everything You Need to Understand Your Business
          </Typography>
          <Typography
            align="center"
            sx={{ color: 'secondary.main', fontSize: { xs: '1rem', md: '1.1rem' } }}
          >
            No complicated setup. No technical skills required. Just connect and
            start getting answers.
          </Typography>
        </Container>
      </Box>

      {/* Features Grid */}
      <Container maxWidth="lg" sx={{ py: { xs: 5, md: 8 }, px: { xs: 2, md: 3 } }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <Card
              sx={{
                height: '100%',
                p: 3,
                bgcolor: 'background.paper',
                border: 1,
                borderColor: 'divider',
              }}
            >
              <Typography
                variant="h6"
                sx={{ color: 'secondary.main', fontWeight: 700, mb: 2 }}
              >
                Connect Your Tools
              </Typography>
              <Typography sx={{ color: 'secondary.main', mb: 2 }}>
                Link your business apps in minutes:
              </Typography>
              <Box sx={{ pl: 2 }}>
                <Typography sx={{ color: 'secondary.main', fontSize: '0.9rem' }}>
                  • QuickBooks, Xero, FreshBooks
                </Typography>
                <Typography sx={{ color: 'secondary.main', fontSize: '0.9rem' }}>
                  • Shopify, WooCommerce, Square
                </Typography>
                <Typography sx={{ color: 'secondary.main', fontSize: '0.9rem' }}>
                  • Stripe, PayPal, payment processors
                </Typography>
                <Typography sx={{ color: 'secondary.main', fontSize: '0.9rem' }}>
                  • MailChimp, HubSpot, marketing tools
                </Typography>
                <Typography sx={{ color: 'secondary.main', fontSize: '0.9rem' }}>
                  • Google Sheets, Excel files
                </Typography>
                <Typography sx={{ color: 'secondary.main', fontSize: '0.9rem' }}>
                  • And 100+ more integrations
                </Typography>
              </Box>
              <Typography
                sx={{ color: 'secondary.main', mt: 2, fontStyle: 'italic' }}
              >
                Everything syncs automatically. No manual updates needed.
              </Typography>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card
              sx={{
                height: '100%',
                p: 3,
                bgcolor: 'background.paper',
                border: 1,
                borderColor: 'divider',
              }}
            >
              <Typography
                variant="h6"
                sx={{ color: 'secondary.main', fontWeight: 700, mb: 2 }}
              >
                Smart Alerts
              </Typography>
              <Typography sx={{ color: 'secondary.main', mb: 2 }}>
                Get notified when things that matter happen:
              </Typography>
              <Box sx={{ pl: 2 }}>
                <Typography sx={{ color: 'secondary.main', fontSize: '0.9rem' }}>
                  • Sales dropping below target
                </Typography>
                <Typography sx={{ color: 'secondary.main', fontSize: '0.9rem' }}>
                  • Inventory running low
                </Typography>
                <Typography sx={{ color: 'secondary.main', fontSize: '0.9rem' }}>
                  • Cash flow concerns
                </Typography>
                <Typography sx={{ color: 'secondary.main', fontSize: '0.9rem' }}>
                  • Unusual spending patterns
                </Typography>
                <Typography sx={{ color: 'secondary.main', fontSize: '0.9rem' }}>
                  • Big opportunities appearing
                </Typography>
                <Typography sx={{ color: 'secondary.main', fontSize: '0.9rem' }}>
                  • Customer behavior changes
                </Typography>
              </Box>
              <Typography
                sx={{ color: 'secondary.main', mt: 2, fontStyle: 'italic' }}
              >
                Receive alerts via text, email, or Slack: your choice.
              </Typography>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card
              sx={{
                height: '100%',
                p: 3,
                bgcolor: 'background.paper',
                border: 1,
                borderColor: 'divider',
              }}
            >
              <Typography
                variant="h6"
                sx={{ color: 'secondary.main', fontWeight: 700, mb: 2 }}
              >
                See What is Coming
              </Typography>
              <Typography sx={{ color: 'secondary.main', mb: 2 }}>
                Make smarter decisions with forecasting:
              </Typography>
              <Box sx={{ pl: 2 }}>
                <Typography sx={{ color: 'secondary.main', fontSize: '0.9rem' }}>
                  • Predict next month sales
                </Typography>
                <Typography sx={{ color: 'secondary.main', fontSize: '0.9rem' }}>
                  • Plan for busy seasons
                </Typography>
                <Typography sx={{ color: 'secondary.main', fontSize: '0.9rem' }}>
                  • Manage cash flow
                </Typography>
                <Typography sx={{ color: 'secondary.main', fontSize: '0.9rem' }}>
                  • Optimize inventory
                </Typography>
                <Typography sx={{ color: 'secondary.main', fontSize: '0.9rem' }}>
                  • Schedule staff effectively
                </Typography>
                <Typography sx={{ color: 'secondary.main', fontSize: '0.9rem' }}>
                  • Budget with confidence
                </Typography>
              </Box>
              <Typography
                sx={{ color: 'secondary.main', mt: 2, fontStyle: 'italic' }}
              >
                Based on your actual business patterns. No guesswork.
              </Typography>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card
              sx={{
                height: '100%',
                p: 3,
                bgcolor: 'background.paper',
                border: 1,
                borderColor: 'divider',
              }}
            >
              <Typography
                variant="h6"
                sx={{ color: 'secondary.main', fontWeight: 700, mb: 2 }}
              >
                Simple Reports
              </Typography>
              <Typography sx={{ color: 'secondary.main', mb: 2 }}>
                Get reports you can actually understand:
              </Typography>
              <Box sx={{ pl: 2 }}>
                <Typography sx={{ color: 'secondary.main', fontSize: '0.9rem' }}>
                  • Daily, weekly, or monthly emails
                </Typography>
                <Typography sx={{ color: 'secondary.main', fontSize: '0.9rem' }}>
                  • Written in plain English
                </Typography>
                <Typography sx={{ color: 'secondary.main', fontSize: '0.9rem' }}>
                  • Focus on what matters to you
                </Typography>
                <Typography sx={{ color: 'secondary.main', fontSize: '0.9rem' }}>
                  • Share with your team instantly
                </Typography>
                <Typography sx={{ color: 'secondary.main', fontSize: '0.9rem' }}>
                  • View on phone, tablet, or computer
                </Typography>
                <Typography sx={{ color: 'secondary.main', fontSize: '0.9rem' }}>
                  • Export to PDF or Excel
                </Typography>
              </Box>
              <Typography
                sx={{ color: 'secondary.main', mt: 2, fontStyle: 'italic' }}
              >
                No confusing charts or technical jargon.
              </Typography>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card
              sx={{
                height: '100%',
                p: 3,
                bgcolor: 'background.paper',
                border: 1,
                borderColor: 'divider',
              }}
            >
              <Typography
                variant="h6"
                sx={{ color: 'secondary.main', fontWeight: 700, mb: 2 }}
              >
                Ask Questions
              </Typography>
              <Typography sx={{ color: 'secondary.main', mb: 2 }}>
                Get instant answers about your business:
              </Typography>
              <Box sx={{ pl: 2 }}>
                <Typography sx={{ color: 'secondary.main', fontSize: '0.9rem' }}>
                  • "Which products are most profitable?"
                </Typography>
                <Typography sx={{ color: 'secondary.main', fontSize: '0.9rem' }}>
                  • "Why did sales drop last week?"
                </Typography>
                <Typography sx={{ color: 'secondary.main', fontSize: '0.9rem' }}>
                  • "Who are my best customers?"
                </Typography>
                <Typography sx={{ color: 'secondary.main', fontSize: '0.9rem' }}>
                  • "What is my average order value?"
                </Typography>
                <Typography sx={{ color: 'secondary.main', fontSize: '0.9rem' }}>
                  • "How is my cash flow trending?"
                </Typography>
              </Box>
              <Typography
                sx={{ color: 'secondary.main', mt: 2, fontStyle: 'italic' }}
              >
                Just type your question. Get an answer in seconds.
              </Typography>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card
              sx={{
                height: '100%',
                p: 3,
                bgcolor: 'background.paper',
                border: 1,
                borderColor: 'divider',
              }}
            >
              <Typography
                variant="h6"
                sx={{ color: 'secondary.main', fontWeight: 700, mb: 2 }}
              >
                Bank-Level Security
              </Typography>
              <Typography sx={{ color: 'secondary.main', mb: 2 }}>
                Your data is protected:
              </Typography>
              <Box sx={{ pl: 2 }}>
                <Typography sx={{ color: 'secondary.main', fontSize: '0.9rem' }}>
                  • 256-bit encryption (same as banks)
                </Typography>
                <Typography sx={{ color: 'secondary.main', fontSize: '0.9rem' }}>
                  • Secure cloud hosting on Microsoft Azure
                </Typography>
                <Typography sx={{ color: 'secondary.main', fontSize: '0.9rem' }}>
                  • Regular security audits
                </Typography>
                <Typography sx={{ color: 'secondary.main', fontSize: '0.9rem' }}>
                  • Two-factor authentication
                </Typography>
                <Typography sx={{ color: 'secondary.main', fontSize: '0.9rem' }}>
                  • Role-based access for team members
                </Typography>
                <Typography sx={{ color: 'secondary.main', fontSize: '0.9rem' }}>
                  • Automatic backups
                </Typography>
              </Box>
              <Typography
                sx={{ color: 'secondary.main', mt: 2, fontStyle: 'italic' }}
              >
                We never sell or share your data. Ever.
              </Typography>
            </Card>
          </Grid>
        </Grid>

        {/* CTA Section */}
        <Box sx={{ textAlign: 'center', mt: 6 }}>
          <Button
            variant="contained"
            size="large"
            onClick={handleSignIn}
            sx={{
              bgcolor: 'accent.main',
              color: 'accent.contrastText',
              py: 2,
              px: { xs: 3, md: 6 },
              fontSize: { xs: '1rem', md: '1.2rem' },
              fontWeight: 700,
              '&:hover': { bgcolor: 'accent.dark' },
            }}
          >
            Start Free Trial
          </Button>
          <Typography sx={{ mt: 2, color: 'secondary.main' }}>
            14 days free. No credit card required.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Features;
